import React, { JSX, useEffect, useId, useState } from 'react'

const display = (value: number | null): string => value == null ? '' : String(value)

export const TextSizeInput = ({
  label,
  value,
  defaultPx,
  min,
  max,
  onChange,
  help,
  testId
}: {
  label: string
  value: number | null
  defaultPx: number | null
  min: number
  max: number
  onChange: (next: number | null) => void
  help?: React.ReactNode
  testId?: string
}): JSX.Element => {
  const id = useId()
  const [text, setText] = useState(display(value))
  const [focused, setFocused] = useState(false)

  useEffect(() => {
    if (!focused) setText(display(value))
  }, [value, focused])

  const commit = (): void => {
    const trimmed = text.trim()
    if (trimmed === '') {
      setText('')
      if (value != null) onChange(null)
      return
    }
    const parsed = Number.parseInt(trimmed, 10)
    if (Number.isNaN(parsed)) {
      setText(display(value))
      return
    }
    const bounded = Math.min(max, Math.max(min, parsed))
    setText(String(bounded))
    if (bounded !== value) onChange(bounded)
  }

  return (
    <div>
      <label htmlFor={id} className='mb-1.5 block text-xs font-medium uppercase tracking-wider text-dm-text-accent'>
        {label}
      </label>
      <div className='flex items-center gap-2'>
        <div className='relative w-28'>
          <input
            id={id}
            data-testid={testId}
            type='text'
            inputMode='numeric'
            value={text}
            placeholder={defaultPx == null ? 'Auto' : String(defaultPx)}
            onChange={(e) => {
              const raw = e.target.value
              if (/^\d*$/.test(raw)) setText(raw)
            }}
            onFocus={() => setFocused(true)}
            onBlur={() => {
              setFocused(false)
              commit()
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit()
            }}
            className='w-full rounded-md border border-white/10 bg-dm-bg-accent/40 py-2 pl-3 pr-8 text-xs text-dm-text-primary placeholder:text-dm-text-accent/70 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
          />
          <span aria-hidden='true' className='pointer-events-none absolute inset-y-0 right-3 flex items-center text-[11px] text-dm-text-accent'>
            px
          </span>
        </div>
        <button
          type='button'
          data-testid={testId == null ? undefined : `${testId}-default`}
          onClick={() => {
            setText('')
            onChange(null)
          }}
          disabled={value == null}
          title='Use the default size'
          className='rounded-md border border-white/10 px-2.5 py-2 text-[11px] text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-50'
        >
          Default
        </button>
      </div>
      {help != null && <p className='mt-1 text-[11px] text-dm-text-accent'>{help}</p>}
    </div>
  )
}
